export interface RecipientPreview {
  blockingReasons?: string[];
  warnings?: string[];
  count: number;
  totalMembers?: number;
  batches: number;
  sample: Array<{ email: string; tier: string }>;
  sendMode: string;
  excluded?: {
    excludedSegments: number;
    suppressed: number;
    exclusionList: number;
  };
}

const REASONS = {
  excludedSegments: 'In excluded segments',
  suppressed: 'Unsubscribed, bounced, complained or otherwise suppressed',
  exclusionList: 'On the exclusion list',
};

export function RecipientSummary({ recipients }: { recipients: RecipientPreview }) {
  const live = recipients.sendMode === 'live';
  return (
    <section
      aria-label="Recipient summary"
      aria-live="polite"
      className={`space-y-3 rounded-lg border p-4 ${recipients.count ? 'border-sky-200 bg-sky-50/50' : 'border-amber-200 bg-amber-50/60'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold">
            {recipients.count.toLocaleString()} eligible recipients
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {recipients.totalMembers !== undefined && (
              <>{recipients.totalMembers.toLocaleString()} unique segment members · </>
            )}
            {recipients.batches.toLocaleString()} {recipients.batches === 1 ? 'batch' : 'batches'}
          </p>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-sm font-medium ${live ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}
        >
          Mode:{' '}
          {live ? 'Live' : recipients.sendMode === 'sandbox' ? 'Sandbox' : recipients.sendMode}
        </span>
      </div>
      <p className="text-sm">
        {live
          ? 'Live mode sends email to eligible contacts.'
          : recipients.sendMode === 'sandbox'
            ? 'Sandbox mode redirects email to the configured test inbox.'
            : 'Check the sending mode before scheduling.'}
      </p>
      {recipients.blockingReasons?.map((reason) => (
        <p key={reason} role="alert" className="rounded bg-red-50 p-2 text-sm text-red-800">
          {reason}
        </p>
      ))}
      {recipients.warnings?.map((warning) => (
        <p key={warning} className="rounded bg-amber-50 p-2 text-sm text-amber-900">
          {warning}
        </p>
      ))}
      {recipients.excluded && Object.values(recipients.excluded).some((n) => n > 0) && (
        <div className="space-y-2 border-t pt-3 text-sm">
          <p className="font-medium">Why some members won’t receive this email</p>
          <ul className="space-y-1">
            {Object.entries(REASONS).map(([key, label]) => {
              const count = recipients.excluded![key as keyof typeof REASONS];
              return count > 0 ? (
                <li key={key}>
                  {label}: <strong>{count.toLocaleString()}</strong>
                </li>
              ) : null;
            })}
          </ul>
        </div>
      )}
      {recipients.count === 0 && (
        <p className="text-sm font-medium text-amber-900">
          {recipients.totalMembers === 0
            ? 'The selected segments have no members. Add members or choose another segment.'
            : 'No eligible recipients right now. Review the exclusions or choose another audience.'}
        </p>
      )}
      {recipients.sample.length > 0 && (
        <p className="break-words text-sm text-muted-foreground">
          Sample:{' '}
          {recipients.sample
            .slice(0, 3)
            .map((r) => r.email)
            .join(', ')}
        </p>
      )}
    </section>
  );
}
